import type { ComponentType } from 'react';
import { Award, Lightbulb } from 'lucide-react';
import { Bell, Briefcase, Building, ClipboardCheck, FileStack, LayoutDashboard, Package, ScrollText, Settings, Sparkles, Zap, type LucideProps } from 'lucide-react';
import type { Capability } from '@cop/shared';
import { canAny } from '@/lib/capabilities';
import { isModuleEnabled, moduleForPath, moduleLabel } from '@/lib/modules';
import type { Principal } from '@/lib/types';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  /** Visible if the principal holds ANY of these capabilities (empty = always). */
  anyOf: Capability[];
  end?: boolean;
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
}

export const STAFF_NAV: NavGroup[] = [
  {
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, anyOf: [] },
      { to: '/clients', label: 'Clients', icon: Building, anyOf: ['client.view'] },
      { to: '/engagements', label: 'Engagements', icon: Briefcase, anyOf: ['engagement.view'] },
      { to: '/review', label: 'Review queue', icon: ClipboardCheck, anyOf: ['form.review', 'asset.review', 'access.verify'] },
    ],
  },
  {
    label: 'Library',
    items: [
      // F01–F04 onboarding forms, the form builder, versions and preview live here.
      { to: '/library/templates', label: 'Forms & templates', icon: FileStack, anyOf: ['templates.view'] },
      { to: '/library/packages', label: 'Packages', icon: Package, anyOf: ['templates.view', 'packages.manage'] },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { to: '/automation', label: 'Automation', icon: Zap, anyOf: ['automation.manage'] },
      { to: '/audit', label: 'Audit & exports', icon: ScrollText, anyOf: ['audit.view', 'export.create'] },
      { to: '/updates', label: 'Updates & announcements', icon: Sparkles, anyOf: [] },
      { to: '/notifications', label: 'Notifications', icon: Bell, anyOf: [] },
      { to: '/settings', label: 'Settings', icon: Settings, anyOf: ['tenant.settings.manage', 'staff.manage', 'roles.manage', 'tenant.billing.manage'] },
    ],
  },
  { label: 'More', items: [
    { to: '/more/products', label: 'Our Other Products', icon: Package, anyOf: [] },
    { to: '/more/suggest-feature', label: 'Suggest Feature', icon: Lightbulb, anyOf: [] },
    { to: '/more/gamification', label: 'Badges & XP', icon: Award, anyOf: [] },
  ] },
];

/** Single-route modules whose nav label the platform may rename (library covers two items, so it keeps its labels). */
const RELABEL_MODULES = new Set(['automation', 'audit', 'review']);

export function visibleNav(principal: Principal | null): NavGroup[] {
  return STAFF_NAV.map((g) => ({
    ...g,
    items: g.items
      .filter((i) => i.anyOf.length === 0 || canAny(principal, i.anyOf))
      // Modules disabled in Platform → Appearance are hidden (the API also rejects them with MODULE_DISABLED).
      .filter((i) => {
        const key = moduleForPath(i.to);
        return !key || isModuleEnabled(key);
      })
      .map((i) => {
        const key = moduleForPath(i.to);
        return key && RELABEL_MODULES.has(key) ? { ...i, label: moduleLabel(key, i.label) } : i;
      }),
  })).filter((g) => g.items.length > 0);
}
